/**
 * @file ExperimentTable.tsx
 * @description This file contains the ExperimentTable component,
 * which is a generic component for displaying and editing experiments.
 */

import React from "react";
import { RouteComponentProps } from "@reach/router";
import { Skeleton, TableSkeleton } from "../Components/Skeleton";
import { ModalEditTable } from "../Components/ModalEditTable";
import "./ExperimentTable.scss";
import { ModernPendingChangesControls } from "./ModernPendingChanges/ModernPendingChangesControls";
import { ModernPendingChangesPanel } from "./ModernPendingChanges/ModernPendingChangesPanel";
import { GroupedDataRow } from "./types";
import {
  CompanyExperiment,
  SpecialUser,
  UserExperiment,
} from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import { Header } from "../Components/StickyTable";
import { useGroupedExperimentTable } from "./useGroupedExperimentTable";
import { useSelector } from "react-redux";
import * as UserRedux from "../redux/user";
import { EXPERIMENT_ROW_HEIGHT } from "./utils";

export interface ExperimentTableProps<T extends UserExperiment | CompanyExperiment | SpecialUser> {
  options: {
    headers: Header[];
    name: string;
    addButtonText: string;
    createNewRow: (groupedColumn: string, groupByColumn: string) => T;
    groupByColumn: keyof T;
    groupedColumn: keyof T;
    expectedKeys: (keyof T & string)[];
    jsonField: "user_experiments" | "company_experiments" | "special_users";
    modalTitle?: string;
  };
}

const ExperimentTable = <T extends UserExperiment | CompanyExperiment | SpecialUser>(
  props: RouteComponentProps & ExperimentTableProps<T>
): React.ReactElement => {
  const { options } = props;
  const {
    headers,
    name,
    addButtonText,
    createNewRow,
    groupByColumn,
    groupedColumn,
    expectedKeys,
    jsonField,
    modalTitle,
  } = options;

  const {
    originalData,
    tableData,
    setTableData,
    newExperiments,
    updatedExperiments,
    deletedExperiments,
    hasPendingChanges,
    showPendingChanges,
    setShowPendingChanges,
    saving,
    save,
    clearAllChanges,
    deletedRows,
    setDeletedRows,
    selectorOptions,
  } = useGroupedExperimentTable<T, typeof groupedColumn>(
    jsonField,
    expectedKeys,
    groupByColumn,
    groupedColumn,
    createNewRow
  );

  const isAdmin = useSelector(UserRedux.isAdminSelector);

  return (
    <div className="experimentTablePageContainer">
      <div className="experimentTableOutline">
        {tableData ? (
          <div className="experimentTableContainer">
            <ModalEditTable<GroupedDataRow<T, typeof groupedColumn>>
              name={name}
              className="experimentTable"
              rowHeight={EXPERIMENT_ROW_HEIGHT}
              headers={headers}
              tableData={tableData}
              setTableData={setTableData}
              selectorOptions={selectorOptions}
              filterBar2
              deletedRows={deletedRows}
              showModalOnAdd={true}
              setDeletedRows={setDeletedRows}
              customFooterButtons={[]}
              addButtonText={addButtonText}
              showReorderActions={false}
              readOnly={!isAdmin}
              enableAdd={isAdmin}
              modalTitle={modalTitle}
            />
            {isAdmin && (
              <ModernPendingChangesControls
                pendingChangesCount={
                  updatedExperiments.length + newExperiments.length + deletedExperiments.length
                }
                hasPendingChanges={hasPendingChanges}
                setShowPendingChanges={setShowPendingChanges}
                saveChanges={save}
                isSaving={saving}
                clearAllChanges={clearAllChanges}
              />
            )}
          </div>
        ) : (
          <Skeleton>
            <TableSkeleton />
          </Skeleton>
        )}
      </div>
      {showPendingChanges && isAdmin && (
        <ModernPendingChangesPanel
          originalData={originalData || {}}
          pendingChanges={{
            newRows: newExperiments,
            editedRows: updatedExperiments,
            deletedRows: deletedExperiments,
          }}
          showPendingChanges={showPendingChanges}
          setShowPendingChanges={setShowPendingChanges}
          headers={headers}
        />
      )}
    </div>
  );
};

export default ExperimentTable;
